import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useRouteMatch } from 'react-router-dom';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';

import useUserDoc from '../../hooks/useUserDoc';
import useToolsNavigation from '../../hooks/useToolsNavigation';
import useSessionStore from '../../../hooks/useSessionStore';
import LoadingPage from '../../../components/LoadingPage';
import BackButton from '../BackButton';
import ManageClientContext from '../../context/ManageClientContext';
import config from '../../../config';
import { StyledTabs } from '../../pages/MealsPlanner/styles';
import UserConfig from '../../../Model/UserConfig';
import useComponentMounted from '../../../hooks/useComponentMounted';
import IconTabBar from '../IconTabBar';

import UserInfoSection from './components/UserInfoSection';
import MobileViewModal from './components/MobileView';
import clientInfoTabs from './tabs';
import {
  clientInfoTabsConfig,
  quickInfoTabsConfig,
} from './utils';
import {
  TabHeaderRow,
  HeaderContainer,
  TabButtonsContainer,
  ClientContainer,
  ClientSideContainer,
  ClientRightContainer,
} from './styles';
import texts from './texts.json';

const NEW_APP_VERSION = '3';

const ClientInfoView = ({
  clientIdForQAP,
  isQAP,
  selectedQAPTab,
}) => {
  const { navigateBack } = useToolsNavigation();
  const { isCoachAssistant, isAdmin } = useSessionStore();
  const {
    selectedTab: clientViewTab,
    setSelectedTab: setclientViewSelectedTab,
  } = useContext(ManageClientContext);

  const {
    params: {
      clientId: clientIdForClientView,
      userId: coachId,
    },
  } = useRouteMatch();

  const [showMobileViewModal, setShowMobileViewModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(selectedQAPTab || clientViewTab);
  const clientId = clientIdForQAP || clientIdForClientView;

  const {
    isReady: isClientDocReady,
    userDoc: clientDoc,
  } = useUserDoc(clientId, { viewAsCoachId: coachId });

  const isTracksVisible = isCoachAssistant || isAdmin;

  const filteredQuickInfoTabsConfig = useMemo(() => {
    if (isTracksVisible) {
      return quickInfoTabsConfig;
    }
    const { [clientInfoTabs.TRACK_ASSIGNMENTS]: _, ...rest } = quickInfoTabsConfig;
    return rest;
  }, [isTracksVisible]);

  const isComponentMountedRef = useComponentMounted();
  const [userConfigDoc, setUserConfigDoc] = useState(null);

  const loadMobileAppView = useCallback(async () => {
    /*
      The first time the modal is opened, we need to load the user config doc. After that, we reuse the same ref to
      avoid loading the doc multiple times. Also, this avoids loading the doc when the user page is first loaded, and
      we only fetch it once we click on the client's view button.
    */
    if (!userConfigDoc) {
      // load the user config doc
      const userConfigDocument = await UserConfig.getConfigById(clientId);

      if (isComponentMountedRef.current) {
        setUserConfigDoc(userConfigDocument);
      }
    }

    setShowMobileViewModal(true);
  }, [clientId, isComponentMountedRef, userConfigDoc]);

  const mobileUrl = useMemo(() => {
    const appVersion = userConfigDoc?.data?.appVersion?.split('.')[0];

    const baseUrl = appVersion === NEW_APP_VERSION
      ? config.mobileAppURLV2
      : config.mobileAppURL;

    return `${baseUrl}/u/${clientId}/home?coachId=${coachId}`;
  }, [clientId, userConfigDoc, coachId]);

  const tabContent = useMemo(() => {
    const { component: TabContent, props } = isQAP
      ? filteredQuickInfoTabsConfig[selectedTab]
      : clientInfoTabsConfig[selectedTab];

    // Keyed on the relationship so a coach switch remounts the tab.
    return (
      <TabContent
        key={`${clientId}-${coachId}`}
        user={clientId}
        userDoc={clientDoc}
        coachId={coachId}
        {...props}
      />
    );
  }, [
    selectedTab,
    isQAP,
    filteredQuickInfoTabsConfig,
    clientDoc,
    clientId,
    coachId,
  ]);

  const onTabChange = useCallback((value) => {
    setSelectedTab(value);

    if (!isQAP) {
      setclientViewSelectedTab(value);
    }
  }, [
    isQAP,
    setclientViewSelectedTab,
  ]);

  if (!isClientDocReady) {
    return <LoadingPage />;
  }

  return (
    <>
      <ClientContainer>
        <ClientSideContainer>
          <UserInfoSection
            clientId={clientId}
            userDoc={clientDoc}
            showMobileViewModal={loadMobileAppView}
            isQAP={isQAP}
          />
        </ClientSideContainer>
        <ClientRightContainer>
          <HeaderContainer>
            <TabHeaderRow>
              {isQAP ? (
                <IconTabBar
                  tabsConfig={filteredQuickInfoTabsConfig}
                  selectedTab={selectedTab}
                  onSelectTab={onTabChange}
                />
              ) : (
                <StyledTabs
                  tabsConfig={clientInfoTabsConfig}
                  selectedTab={selectedTab}
                  onSelectTab={onTabChange}
                />
              )}
              {!isQAP && (
                <TabButtonsContainer>
                  <BackButton
                    label={texts.goBack}
                    onClick={navigateBack}
                  />
                </TabButtonsContainer>
              )}
            </TabHeaderRow>
          </HeaderContainer>
          {tabContent}
        </ClientRightContainer>
      </ClientContainer>

      <MobileViewModal
        showModal={showMobileViewModal}
        onClose={() => setShowMobileViewModal(false)}
        mobileUrl={mobileUrl}
      />
    </>
  );
};

ClientInfoView.propTypes = {
  isQAP: PropTypes.bool,
  clientIdForQAP: PropTypes.string,
  selectedQAPTab: PropTypes.string,
};

ClientInfoView.defaultProps = {
  isQAP: false,
  selectedQAPTab: '',
  clientIdForQAP: '',
};

export default compose(
  observer,
)(ClientInfoView);
